import { useRef } from "react";
import { Upload, Download, Trash2, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { brl, pct, num, fmtDateTime } from "@/lib/format";
import { Button, Card } from "@/components/kit";

const deltaColor = (v) => (v === null || v === undefined ? undefined : v > 0 ? "var(--danger)" : v < 0 ? "var(--success)" : undefined);

export async function downloadTemplate() {
  const blob = await api("/sales/template", { responseType: "blob" });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: "modelo-vendas.xlsx" });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function SalesImport({ data, onFile, onRemove, onTemplate, busy }) {
  const ref = useRef();
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onFile(f);
  };
  return (
    <Card className="flex flex-col gap-3" data-testid="sales-import-card">
      <div className="flex items-center gap-3">
        <FileSpreadsheet size={20} style={{ color: "var(--brand)" }} />
        <div className="flex-1 min-w-0">
          <div className="h3">Vendas do período</div>
          <div className="caption truncate" data-testid="sales-import-status">
            {data.has_sales ? `${data.filename || "Planilha"} · importada ${fmtDateTime(data.imported_at)} por ${data.imported_by}` : "Importe a planilha de vendas do caixa (.xlsx) com as colunas Nome Prod, Cód. Prod e Qtd."}
          </div>
        </div>
      </div>
      <input ref={ref} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv" className="hidden" onChange={pick} data-testid="sales-file-input" />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" icon={Upload} loading={busy} onClick={() => ref.current.click()} data-testid="sales-import-button">{data.has_sales ? "Substituir planilha" : "Importar planilha"}</Button>
        <Button size="sm" variant="ghost" icon={Download} onClick={onTemplate} data-testid="sales-template-button">Baixar modelo</Button>
        {data.has_sales && <Button size="sm" variant="ghost" icon={Trash2} onClick={onRemove} data-testid="sales-remove-button">Remover</Button>}
      </div>
      <p className="caption">Formato: planilha Excel (.xlsx) com as colunas “Nome Prod”, “Cód. Prod” e “Qtd.” · o item é reconhecido pelo código do PDV (da ficha técnica ou do produto) ou pelo nome. Itens vendidos direto (bebidas etc.) são ligados ao produto do cadastro.</p>
    </Card>
  );
}

function Kpi({ label, value, sub, testid, color }) {
  return (
    <div className="rounded-xl p-4" style={{ background: "var(--surface-alt)" }} data-testid={testid}>
      <div className="caption">{label}</div>
      <div className="h2 tnum mt-1" style={color ? { color } : undefined}>{value}</div>
      {sub && <div className="caption tnum mt-0.5">{sub}</div>}
    </div>
  );
}

export function ComparisonKpis({ t, target }) {
  return (
    <Card className="flex flex-col gap-4" data-testid="cmv-comparison">
      <div className="h3">CMV teórico x real</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="CMV teórico (fichas × vendas)" value={brl(t.theoretical_cmv)} sub={`${pct(t.theoretical_pct)} do faturamento`} testid="kpi-theoretical" />
        <Kpi label="CMV real (conferência)" value={brl(t.real_cmv)} sub={`${pct(t.real_pct)} · meta ${pct(target, 1)}`} testid="kpi-real" />
        <Kpi label="Desvio (real − teórico)" value={brl(t.deviation)} sub={t.deviation_pct === null ? "—" : `${t.deviation_pct > 0 ? "+" : ""}${pct(t.deviation_pct)} sobre o teórico`} color={deltaColor(t.deviation)} testid="kpi-deviation" />
        <Kpi label="Desvio em pontos de CMV" value={t.deviation_pp === null ? "—" : `${t.deviation_pp > 0 ? "+" : ""}${num(t.deviation_pp, 1)} p.p.`} sub={`${num(t.sold_portions, 0)} porções · ${num(t.sold_products || 0, 0)} itens de revenda`} color={deltaColor(t.deviation_pp)} testid="kpi-deviation-pp" />
      </div>
      <p className="caption">Desvio positivo = consumo acima do previsto pelas fichas (desperdício, porções maiores, perdas não registradas ou vendas não lançadas). Perdas registradas na conferência: {brl(t.losses_total)}.</p>
    </Card>
  );
}

export function UnmatchedSales({ rows, recipes, products, onMap, busy }) {
  if (!rows.length) return null;
  const pick = (name, v) => {
    const [kind, id] = v.split(":");
    onMap(name, kind === "r" ? { recipe_id: id } : { product_id: id });
  };
  return (
    <Card className="flex flex-col gap-3" data-testid="unmatched-sales">
      <div className="flex items-center gap-2"><AlertTriangle size={17} style={{ color: "var(--accent)" }} /><div className="h3">Itens sem ficha ou produto ({rows.length})</div></div>
      <p className="caption">Vincule cada item vendido a uma ficha técnica ou a um produto de revenda · o vínculo é lembrado nas próximas importações. Dica: cadastre o código do PDV no produto ou na ficha.</p>
      <div className="divide">
        {rows.map((r) => (
          <div key={r.name} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`unmatched-row-${r.name}`}>
            <div className="flex-1 min-w-[140px]"><div className="label">{r.name}</div><div className="caption tnum">{num(r.qty)} {r.qty === 1 ? "vendido" : "vendidos"}{r.code ? ` · cód. ${r.code}` : ""}</div></div>
            <select className="input input-sm max-w-[240px]" defaultValue="" disabled={busy} onChange={(e) => e.target.value && pick(r.name, e.target.value)} data-testid={`unmatched-select-${r.name}`}>
              <option value="">Vincular a…</option>
              <optgroup label="Fichas técnicas">{recipes.map((x) => <option key={x.id} value={`r:${x.id}`}>{x.name}</option>)}</optgroup>
              <optgroup label="Produtos (revenda)">{products.map((x) => <option key={x.id} value={`p:${x.id}`}>{x.name}</option>)}</optgroup>
            </select>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function SalesTable({ rows }) {
  return (
    <Card className="flex flex-col gap-3" data-testid="sales-table">
      <div className="h3">Itens vendidos · custo teórico</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="caption text-left"><th className="py-2">Item</th><th className="text-right">Vendidos</th><th className="text-right">Custo unit.</th><th className="text-right">CMV teórico</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`sales-row-${r.recipe_id || r.product_id}`}>
                <td className="py-2.5"><div className="label">{r.recipe_name}{r.kind === "product" && <span className="badge badge-muted !py-0 ml-2" data-testid={`sales-resale-badge-${r.product_id}`}>Revenda</span>}</div>{r.recipe_name !== r.name && <div className="caption">{r.name}</div>}</td>
                <td className="text-right tnum">{num(r.qty)}</td><td className="text-right tnum">{brl(r.cost_per_portion)}</td><td className="text-right tnum font-bold">{brl(r.theoretical_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="caption py-3">Nenhuma venda vinculada a fichas ou produtos.</p>}
      </div>
    </Card>
  );
}

const signed = (v, digits) => `${v > 0 ? "+" : ""}${num(v, digits)}`;

function DeviationCard({ r }) {
  return (
    <div className="py-3 flex flex-col gap-1" data-testid={`deviation-card-${r.product_id}`}>
      <div className="flex items-baseline gap-2">
        <span className="label flex-1 min-w-0 truncate">{r.product_name}</span>
        <span className="tnum font-bold" style={{ color: deltaColor(r.diff_value) }}>{r.diff_value === null ? "—" : brl(r.diff_value)}</span>
      </div>
      <div className="caption tnum flex flex-wrap gap-x-3">
        <span>Teórico {num(r.theoretical_qty)} {r.unit}</span>
        <span>Real {r.real_qty === null ? "—" : `${num(r.real_qty)} ${r.unit}`}</span>
        {r.diff_qty !== null && <span style={{ color: deltaColor(r.diff_qty) }}>Desvio {signed(r.diff_qty)}{r.diff_pct !== null ? ` (${signed(r.diff_pct, 0)}%)` : ""}</span>}
        {r.losses_qty > 0 && <span>Perdas {num(r.losses_qty)}</span>}
        {!r.in_recipes && <span>Fora das fichas{r.sales_code ? "" : " · sem cód. PDV"}</span>}
        {r.sold_direct && <span>Venda direta</span>}
      </div>
    </div>
  );
}

export function DeviationTable({ rows, productLevel }) {
  if (!productLevel) {
    return <Card data-testid="deviation-unavailable"><div className="h3 mb-1">Desvio por insumo</div><p className="caption">Esta conferência registra compras por nota fiscal, então o consumo real por produto não é conhecido. Use o modo de compras por quantidade para ver o desvio por insumo.</p></Card>;
  }
  return (
    <Card className="flex flex-col gap-3" data-testid="deviation-table">
      <div className="h3">Desvio por insumo · onde está o desperdício</div>
      <div className="sm:hidden divide" data-testid="deviation-list-mobile">
        {rows.map((r) => <DeviationCard key={r.product_id} r={r} />)}
      </div>
      <div className="overflow-x-auto hidden sm:block">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="caption text-left"><th className="py-2">Insumo</th><th className="text-right">Teórico</th><th className="text-right">Real</th><th className="text-right">Perdas reg.</th><th className="text-right">Desvio</th><th className="text-right">Desvio R$</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.product_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`deviation-row-${r.product_id}`}>
                <td className="py-2.5"><div className="label">{r.product_name}</div>{!r.in_recipes && <div className="caption">Fora das fichas{r.sales_code ? "" : " · sem cód. PDV"}</div>}{r.sold_direct && <div className="caption">Venda direta</div>}{!r.counted && <div className="caption">Não contado na conferência</div>}</td>
                <td className="text-right tnum">{num(r.theoretical_qty)} {r.unit}</td>
                <td className="text-right tnum">{r.real_qty === null ? "—" : `${num(r.real_qty)} ${r.unit}`}</td>
                <td className="text-right tnum">{r.losses_qty ? `${num(r.losses_qty)} ${r.unit}` : "—"}</td>
                <td className="text-right tnum" style={{ color: deltaColor(r.diff_qty) }}>{r.diff_qty === null ? "—" : `${r.diff_qty > 0 ? "+" : ""}${num(r.diff_qty)}${r.diff_pct !== null ? ` (${r.diff_pct > 0 ? "+" : ""}${num(r.diff_pct, 0)}%)` : ""}`}</td>
                <td className="text-right tnum font-bold" style={{ color: deltaColor(r.diff_value) }}>{r.diff_value === null ? "—" : brl(r.diff_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rows.length && <p className="caption py-3">Sem insumos para comparar.</p>}
    </Card>
  );
}
